import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../core/services/AuthStore';
import {
  LayoutDashboard,
  Users,
  Building2,
  Dam,
  UserCircle,
  Send,
  FileText,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ScrollText,
  AlertCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  if (!user) return null;

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const isActive = (path: string) => location.pathname === path;

  // Determinar roles do usuário
  const userRoles = user.roles || [];
  const displayName = user.name?.trim()
    ? user.name.trim().split(/\s+/)[0]
    : user.email;

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'gestor'] },
    { path: '/clients', icon: Building2, label: 'Clientes', roles: ['admin'] },
    { path: '/users', icon: Users, label: 'Usuários', roles: ['admin', 'gestor'] },
    { path: '/dams', icon: Dam, label: 'Barragens', roles: ['admin', 'gestor', 'colaborador'] },
    { path: '/contacts', icon: UserCircle, label: 'Contatos', roles: ['admin', 'gestor', 'colaborador'] },
    { path: '/groups', icon: Users, label: 'Grupos', roles: ['admin', 'gestor', 'colaborador'] },
    { path: '/templates', icon: FileText, label: 'Templates', roles: ['admin', 'gestor', 'colaborador'] },
    { path: '/send-messages', icon: Send, label: 'Enviar Mensagens', roles: ['admin', 'gestor', 'colaborador'] },
    { path: '/logs', icon: ScrollText, label: 'Logs', roles: ['admin', 'gestor'] },
  ];

  const visibleItems = menuItems.filter(item => 
    item.roles.some(role => userRoles.includes(role))
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-900 text-white"
      >
        {isOpen ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed top-0 left-0 z-40 lg:sticky ${
          isOpen ? 'w-64' : 'lg:w-20'
        } bg-slate-900 text-white h-screen flex flex-col transition-transform lg:transition-all duration-300`}
      >
        <div className={`p-6 border-b border-slate-700 flex items-center ${isOpen ? 'justify-between' : 'justify-center'}`}>
          {isOpen && (
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">
                GeoProtege
              </h1>
            </div>
          )}
          {isOpen && (
            <button
              onClick={() => setIsOpen(false)}
              className="hidden lg:block text-slate-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {!isOpen && (
            <button
              onClick={() => setIsOpen(true)}
              className="hidden lg:block text-slate-400 hover:text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        <nav className={`flex-1 ${isOpen ? 'p-4' : 'p-2'}`}>
          <ul className="space-y-2">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isPrimary = item.path === '/send-messages';
              const isLogs = item.path === '/logs';
              return (
                <li key={item.path} className={isPrimary || isLogs ? 'mt-2 pt-2 border-t border-slate-700' : ''}>
                  <Link
                    to={item.path}
                    className={`flex items-center ${isOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-blue-600 text-white'
                        : isPrimary
                        ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-700/50 hover:bg-emerald-600/30'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                    title={item.label}
                  >
                    <Icon className={`shrink-0 ${isPrimary ? 'w-6 h-6' : 'w-5 h-5'}`} />
                    {isOpen && <span className={isPrimary ? 'font-semibold' : ''}>{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={`${isOpen ? 'p-4' : 'p-2'} border-t border-slate-700 mt-auto`}>
          <div className={`${isOpen ? 'block' : 'hidden lg:hidden'} mb-4`}>
            <p className="text-sm text-slate-400">Logado como</p>
            <p className="font-medium truncate">{displayName}</p>
            <p className="text-xs text-slate-400">{userRoles.join(', ')}</p>
          </div>
          <button
            onClick={handleLogout}
            className={`flex items-center ${isOpen ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-lg text-red-400 hover:bg-red-950/30 hover:text-red-300 w-full transition-colors`}
            title="Sair"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {isOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Modal de Confirmação de Logout */}
      <Dialog open={showLogoutModal} onOpenChange={setShowLogoutModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Confirmar Saída</DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-700">
              Tem certeza que deseja sair do sistema?
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Você precisará fazer login novamente para acessar.
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelLogout} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmLogout}
              className="h-11 bg-red-600 hover:bg-red-700 text-white"
            >
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
